// https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/#explicit-rendering

import pDefer, { type DeferredPromise } from 'p-defer';

type GetToken = () => Promise<string | null>;
type FormatRequestOptions = (token: string | null) => Record<string, any>;

type UseInteractionOnlyTurnstileReturn = {
    getToken: GetToken;
    formatRequestOptions: FormatRequestOptions;
};

type Nullable<T> = T | null;

export const TURNSTILE_CONTAINER_ID = 'turnstile-container';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const scriptReadyDefer = pDefer();

export const useInteractionOnlyTurnstile = (action: string): UseInteractionOnlyTurnstileReturn => {
    const config = useRuntimeConfig();
    const { turnstileDisabled, turnstileSiteKey } = config.public;
    const { locale } = useTypedI18n();

    let wrapperElement: Nullable<HTMLElement> = null;
    let widgetId: Nullable<string> = null;
    let getTokenDefer: Nullable<DeferredPromise<string>> = null;

    const { load } = useScript(SCRIPT_URL, {
        trigger: 'manual',
    });

    onBeforeMount(() => {
        if (turnstileDisabled) return;

        load().then(() => scriptReadyDefer.resolve());
    });
    onMounted(async () => {
        if (turnstileDisabled) return Promise.resolve();

        wrapperElement = ((): HTMLElement => {
            const turnstileComponent = document.getElementById(TURNSTILE_CONTAINER_ID);

            if (turnstileComponent) return turnstileComponent;

            const element = document.createElement('div');
            document.body.appendChild(element);

            return element;
        })();

        await scriptReadyDefer.promise;

        widgetId =
            window.turnstile.render(wrapperElement, {
                sitekey: turnstileSiteKey,
                theme: 'light',
                size: 'flexible',
                appearance: 'interaction-only',
                retry: 'auto',
                language: locale.value,
                action,
                callback: (token: string): void => {
                    getTokenDefer?.resolve(token);
                },
                'error-callback': (): void => {
                    getTokenDefer?.reject();
                },
                'expired-callback': (): void => {
                    getTokenDefer = null;
                },
            }) ?? null;
    });

    onBeforeUnmount(() => {
        if (turnstileDisabled) return;

        if (widgetId) {
            window.turnstile.remove(widgetId);
            widgetId = null;
        }

        wrapperElement?.remove();
    });

    const getToken: GetToken = async () => {
        if (turnstileDisabled) return null;

        await scriptReadyDefer.promise;

        if (!widgetId) return null;

        await getTokenDefer?.promise;

        const tokenResolved = !!getTokenDefer && (await isResolved(getTokenDefer.promise));
        const tokenRejected = !!getTokenDefer && (await isRejected(getTokenDefer.promise));

        if (!getTokenDefer || tokenRejected || tokenResolved) {
            getTokenDefer = pDefer<string>();

            // BSD-235 equivalent: token is single-use, reset before re-executing to avoid reuse errors
            if (widgetId) {
                window.turnstile.reset(widgetId);
            } else if (wrapperElement) {
                window.turnstile.execute(wrapperElement);
            }
        }

        return getTokenDefer.promise;
    };

    const formatRequestOptions: FormatRequestOptions = token => {
        if (turnstileDisabled) return {};

        if (!token) throw new Error('Error in turnstile formatRequestOptions: nullish token');

        return { headers: { 'cf-turnstile-response': token } };
    };

    return { getToken, formatRequestOptions };
};
